<template>
  <div class="calc-controls-cargo">
    <mdb-container class="cargo border rounded p-3">
      <calc-controls-cargo-table
        :key="tableKey"
        :pagination="false"
        :columns="cargoTableSchema.columns"
        :groups-from="cargoTableSchema.groupsFrom"
        :cargo-data="cargoMap"
        :invalid-cargo="invalidCargo"
        :cargo-placeholders="cargoPlaceholders"
        :cargo-validation-messages="cargoValidationMessages"
        :highlighted-inputs="highlightedInputs"
        :wrong-mu-codes="wrongMuCodes"
        @cargo-add="cargoCreate()"
        @cargo-remove="cargoRemove($event)"
        @cargo-update="cargoUpdate($event)"
        @input-focus="inputFocus($event)"
        @input-blur="inputBlur()"
        @cargo-populated="cargoPopulated($event)"
      >
      </calc-controls-cargo-table>
    </mdb-container>
    <div class="d-flex flex-row justify-content-around pt-4">
      <mdb-btn
        title="Search for rates for given route and packing"
        gradient="aqua"
        :class="!!invalidCargo.length ? 'button-disabled' : ''"
        rounded
        @click.native.prevent="onCalculateClicked"
      >
        Calculate Rates
      </mdb-btn>
    </div>
  </div>
</template>

<script>
import { mdbSwitch, mdbTab, mdbTabItem, mdbTabPane, mdbTabContent, mdbContainer, mdbBtn } from 'mdbvue'
import CalcControlsCargoTable from './CalcControlsCargoTable'
import { mapState, mapGetters, mapActions } from 'vuex'
import { errorCatch, uid } from '../utils'
import {LocationType, RoutePointTypes} from '../enums'

export default {
  name: 'CalcControlsCargo',
  data () {
    return {
      tableKey: uid(),
      cargoData: {},
      cargoPlaceholders: {},
      cargoValidationMessages: {},
      invalidCargo: [],
      highlightedInputs: {},
      wrongMuCodes: {}
    }
  },
  computed: {
    ...mapState('calc', {
      query: state => state.query,
      measureUnits: state => state.data.measureUnits,
      measureUnitsInfo: state => state.data.measureUnitsInfo
    }),
    ...mapGetters('calc', ['cargoTableSchema', 'groupedMeasureUnits']),
    emptyCargo: {
      get: function () {
        const emptyCargo = {}
        this.cargoTableSchema.columns.forEach(column => {
          emptyCargo[column.code] = null
        })
        return emptyCargo
      }
    },
    cargoMap: {
      get: function () {
        const cargoMap = {}
        Object.keys(this.cargoData).forEach(cid => {
          const cargo = this.cargoData[cid]
          cargoMap[cid] = Object.keys(cargo).map(code => {
            return {mu: code, value: cargo[code]}
          })
        })
        return cargoMap
      }
    }
  },
  methods: {
    ...mapActions('calc', [
      'setData',
      'setRatesData',
      'setFlights',
      'clearFlights',
      'clearRates',
      'clearCargoTypes',
      'clearSelectedRates'
    ]),
    ...mapActions([
      'apiCalculateRates',
      'apiCalculateConnectedRates',
      'apiCalculateCargoParameters',
      'apiCalculateDirectFlights',
      'apiCalculateConnectedFlights'
    ]),
    onCalculateClicked () {
      if (!this.invalidCargo.length) {
        const cargoData = Object.assign({}, this.cargoData)
        this.cargoData = {}
        Object.values(cargoData).forEach(data => {
          this.cargoData[uid()] = data
        })
        this.cargoValidate()
        this.clearSelectedRates().then(() => {
          const calculateParams = {
            qid: null,
            origin: {
              type: this.query.routeByCoordinates.origin ? 'pointed' : 'named',
              useAlternatives: this.query.useAlternatives.origin,
              ...(this.query.routeByCoordinates.origin ? {
                latitude: this.query.routePoints.origin.latitude,
                longitude: this.query.routePoints.origin.longitude
              } : {
                name: this.query.routePoints.origin.name
              })
            },
            destination: {
              type: this.query.routeByCoordinates.destination ? 'pointed' : 'named',
              useAlternatives: this.query.useAlternatives.destination,
              ...(this.query.routeByCoordinates.destination ? {
                latitude: this.query.routePoints.destination.latitude,
                longitude: this.query.routePoints.destination.longitude
              } : {
                name: this.query.routePoints.destination.name
              })
            },
            client: this.query.client,
            agent: this.query.agent,
            cargoTypes: this.query.cargoTypes,
            cargo: Object.values(cargoData).map(data => {
              return Object.keys(data).map(key => {
                return {mu: key, value: data[key]}
              }).filter(item => item.value != null)
            })
          }
          this.apiCalculateRates(calculateParams)
            .then(data => {
              this.setRatesData(data)
              if (data.rates.length === 0) {
                this.apiCalculateConnectedRates(calculateParams)
                  .then(data => {
                    this.setRatesData(data)
                    if (data.rates.length === 0) {
                      this.$notify.warn({
                        title: 'Empty response',
                        message: 'Unfortunately, no suitable rates were found.',
                        position: 'top right',
                        timeOut: 10000,
                        closeButton: true
                      })
                    } else {
                      this.$bus.$emit('calculate-completed')
                    }
                  })
              } else {
                this.$bus.$emit('calculate-completed')
              }
            })
            .catch(errorCatch('Calculate Rates', this))
        })
      } else {
        this.$notify.warn({
          title: 'Validation failed.',
          message: 'Please check Cargo Weight and Size Characteristics.',
          position: 'top right',
          timeOut: 15000,
          closeButton: true
        })
      }
    },
    cargoCreate () {
      const cid = uid()
      this.$set(this.cargoData, cid, Object.assign({}, this.emptyCargo))
      this.cargoValidate(cid)
    },
    cargoRemove (cid) {
      this.$delete(this.cargoData, cid)
      if (this.invalidCargo.includes(cid)) {
        this.invalidCargo = this.invalidCargo.filter(crgId => crgId !== cid)
      }
    },
    cargoUpdate ({ cid, code, value }) {
      value = value || null
      this.$set(this.cargoData[cid], code, value)
      this.cargoValidate(cid)
    },
    cargoPopulated (data) {
      const cargo = {}
      data.forEach(line => {
        const newCargo = {}
        this.cargoTableSchema.columns.forEach((column, index) => {
          newCargo[column.code] = parseFloat(line[index].toString().replace(/,/, '.')) || null
        })
        cargo[uid()] = newCargo
      })
      this.cargoData = cargo
      this.invalidCargo = []
      this.cargoValidate()
    },
    cargoValidate (cid = null) {
      if (cid === null) {
        this.wrongMuCodes = {}
        Object.keys(this.cargoData).forEach(cid => {
          this.cargoValidate(cid)
        })
      } else {
        delete this.wrongMuCodes[cid]
        const rootMeasureUnits = (
          this.groupedMeasureUnits[0] && this.groupedMeasureUnits[0].map(rootMeasureUnit => {
            return this.measureUnitsInfo.find(el => el.code === rootMeasureUnit.code)
          })
        ) || []
        let isValid = true
        const calculateValue = code => {
          const muInfo = this.measureUnitsInfo.find(el => el.code === code)
          let result = null
          if (muInfo && muInfo.formula !== null) {
            let script = ''
            let calc = true
            let variables = {}
            for (let [varName, varCode] of Object.entries(muInfo.variables)) {
              const value = calculateValue(varCode)
              if (!value) {
                calc = false
              }
              variables[varName] = value
            }
            for (let [varName, varValue] of Object.entries(variables)) {
              script += `const ${varName} = ${varValue}\n`
            }
            script += muInfo.formula
            // eslint-disable-next-line
            const value = (calc ? eval(script) : null) || (this.cargoData[cid] && this.cargoData[cid][code]) || null
            if ((this.cargoData[cid][code] && value !== this.cargoData[cid][code]) || (muInfo.important && !value)) {
              // TODO: Omg, do something with it!
              if (!(muInfo.important && !value)) {
                if (!(cid in this.wrongMuCodes)) {
                  this.wrongMuCodes[cid] = []
                }
                this.wrongMuCodes[cid].push(code)
                this.wrongMuCodes[cid].push(...Object.values(muInfo.variables))
              }
              isValid = false
            }
            result = value
          } else {
            result = this.cargoData[cid] && this.cargoData[cid][code]
          }
          if (this.cargoPlaceholders[cid] === undefined) {
            this.cargoPlaceholders[cid] = {}
          }
          this.cargoPlaceholders[cid][code] = result
          const muRestrictions = this.measureUnits.find(mu => mu.code === code)
          if (muRestrictions.min && muRestrictions.min > result) {
            isValid = false
            if (this.cargoValidationMessages[cid] === undefined) {
              this.cargoValidationMessages[cid] = {}
            }
            this.cargoValidationMessages[cid][code] = `Min.: ${muRestrictions.min}`
          } else if (muRestrictions.max && muRestrictions.max < result) {
            isValid = false
            if (this.cargoValidationMessages[cid] === undefined) {
              this.cargoValidationMessages[cid] = {}
            }
            this.cargoValidationMessages[cid][code] = `Max.: ${muRestrictions.max}`
          } else if (this.cargoValidationMessages[cid] && this.cargoValidationMessages[cid][code]) {
            delete this.cargoValidationMessages[cid][code]
            if (this.cargoValidationMessages[cid].length === 0) {
              delete this.cargoValidationMessages[cid]
            }
          }
          return result
        }

        rootMeasureUnits.forEach(rootMeasureUnit => {
          const value = calculateValue(rootMeasureUnit.code)
          if (!value && rootMeasureUnit.important) {
            isValid = false
          }
        })
        if (isValid === false && !this.invalidCargo.includes(cid)) {
          this.invalidCargo.push(cid)
        } else if (isValid === true && this.invalidCargo.includes(cid)) {
          this.invalidCargo = this.invalidCargo.filter(crgId => crgId !== cid)
        }
      }
    },
    inputFocus ({cid, code}) {
      // TODO: Make getter object with uuid keys
      const varCodes = Object.values(this.measureUnitsInfo.find(el => el.code === code).variables)
      this.highlightedInputs = { cid: cid, codes: varCodes }
    },
    inputBlur () {
      this.highlightedInputs = {}
    }
  },
  mounted () {
    this.$bus.$on('query-change', callback => {
      if (!this.query.routePoints[RoutePointTypes.ORIGIN] || !this.query.routePoints[RoutePointTypes.DESTINATION]) {
        return
      }
      this.clearFlights()
        .then(() => {
          this.clearRates()
            .then(() => {
              this.clearCargoTypes()
                .then(() => {
                  const oldMeasureUnits = this.measureUnits.map(measureUnit => measureUnit.code)
                  const params = {
                    origin: {
                      type: this.query.routeByCoordinates.origin ? 'pointed' : 'named',
                      useAlternatives: this.query.useAlternatives.origin,
                      ...(this.query.routeByCoordinates.origin ? {
                        latitude: this.query.routePoints.origin.latitude,
                        longitude: this.query.routePoints.origin.longitude
                      } : {
                        name: this.query.routePoints.origin.name
                      })
                    },
                    destination: {
                      type: this.query.routeByCoordinates.destination ? 'pointed' : 'named',
                      useAlternatives: this.query.useAlternatives.destination,
                      ...(this.query.routeByCoordinates.destination ? {
                        latitude: this.query.routePoints.destination.latitude,
                        longitude: this.query.routePoints.destination.longitude
                      } : {
                        name: this.query.routePoints.destination.name
                      })
                    },
                    client: this.query.client || null
                  }
                  this.apiCalculateCargoParameters(params)
                    .then(cargoParams => {
                      this.setData({entity: 'measureUnits', data: cargoParams.measureUnits})
                      this.setData({entity: 'cargoTypes', data: cargoParams.cargoTypes})
                      const isEmpty = cargoParams.measureUnits.length === 0
                      if (isEmpty) {
                        const flightLocationTypes = [LocationType.AIRPORT, LocationType.CITY]
                        if (
                          flightLocationTypes.includes(this.query.routePoints.destination.type) &&
                          flightLocationTypes.includes(this.query.routePoints.origin.type)
                        ) {
                          const params = {
                            origin: {
                              type: this.query.routeByCoordinates.origin ? 'pointed' : 'named',
                              useAlternatives: this.query.useAlternatives.origin,
                              ...(this.query.routeByCoordinates.origin ? {
                                latitude: this.query.routePoints.origin.latitude,
                                longitude: this.query.routePoints.origin.longitude
                              } : {
                                name: this.query.routePoints.origin.name
                              })
                            },
                            destination: {
                              type: this.query.routeByCoordinates.destination ? 'pointed' : 'named',
                              useAlternatives: this.query.useAlternatives.destination,
                              ...(this.query.routeByCoordinates.destination ? {
                                latitude: this.query.routePoints.destination.latitude,
                                longitude: this.query.routePoints.destination.longitude
                              } : {
                                name: this.query.routePoints.destination.name
                              })
                            }
                          }

                          this.apiCalculateConnectedFlights(params)
                            .then(flights => {
                              if (flights.length !== 0) {
                                this.setFlights(flights)
                              } else {
                                this.$notify.warn({
                                  title: 'Empty response',
                                  message: 'Unfortunately, nothing suitable was found.',
                                  position: 'top right',
                                  timeOut: 10000,
                                  closeButton: true
                                })
                              }
                              this.$bus.$emit('calculate-completed')
                            })
                            .catch(errorCatch('Calculate Connected Flights failed.', this))
                        }
                      }
                      const newMeasureUnits = this.measureUnits.map(measureUnit => measureUnit.code)
                      if (JSON.stringify(oldMeasureUnits) !== JSON.stringify(newMeasureUnits)) {
                        this.cargoData = {}
                        this.cargoPlaceholders = {}
                        this.invalidCargo = []
                        this.highlightedInputs = {}

                        if (!isEmpty) {
                          this.cargoCreate()
                        }
                      }
                      if (callback) {
                        callback()
                      }
                      this.cargoValidate()
                    })
                    .catch(errorCatch('Get Cargo Parameters', this))
                })
            })
        })
    })
    this.$bus.$on('clone-request', ({request}) => {
      this.$bus.$emit(
        'query-change',
        () => {
          let cargo = {}
          request.cargos.forEach(crg => {
            cargo[crg.cid] = Object.assign({}, this.emptyCargo)
            crg.data.forEach(mu => {
              cargo[crg.cid][mu.code] = mu.value
            })
          })
          this.cargoData = cargo
          this.invalidCargo = []
          this.cargoValidate()
          this.tableKey = uid()
        }
      )
    })
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      if (Object.keys(vm.cargoData).length === 0) {
        vm.cargoCreate()
      }
    })
  },
  beforeDestroy: function () {
    this.$bus.$off('query-change')
    this.$bus.$off('clone-request')
  },
  components: {
    mdbTab,
    mdbTabContent,
    mdbTabItem,
    mdbTabPane,
    mdbContainer,
    mdbBtn,
    mdbSwitch,
    CalcControlsCargoTable
  }
}
</script>

<style lang="sass" scoped>
.cargo
  max-width: none

.button-disabled
  opacity: 0.65
  cursor: default

  &:hover
    color: orange!important

.calc-controls-cargo
  animation: fadein 2s

@keyframes fadein
  from
    opacity: 0
  to
    opacity: 1
</style>



// WEBPACK FOOTER //
// src/components/CalcControlsCargo.vue